import React, { useContext, useMemo, useRef } from "react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, Col, Input, Row } from "antd";
import { useLocation, useHistory } from "react-router-dom";
import { debounce } from "lodash";

import { countsScopeKey, countsScopeOf, fetchAllInvoices, fetchAssigneeCounts, fetchInvoiceCounts } from "../store/invoicesSlice";
import { FeatureFlags, FilterOptModel } from "../hooks/featureFlagsProvider";
import { fetchEmailCounts, fetchEmails } from "../store/emailsSlice";
import { searchServiceTeams } from "../store/serviceTeamSlice";
import AssigneeSelect from "../components/AssigneeSelect";
import DebounceSelect from "../components/DebounceSelect";
import AssignToModal from "../components/AssignToModal";
import InvoicesTable from "../components/InvoicesTable";
import StatusFilters from "../components/StatusFilters";
import EmailsTable from "../components/EmailsTable";
import { DEBOUNCE_TIME } from "../utils/constants";
import TagInput from "../components/InputWithTags";
import BlankCard from "../components/BlankCard";
import SearchBar from "../components/SearchBar";
import useSignalR from "../hooks/useSignalr";
import { useAppSelector } from "../store";
import { StatusCounts } from "../utils";
import styled from "styled-components";
import { breakpoint } from "../theme";
import { DocumentTypeEnum } from "../enums/DocumentTypeEnum";
import { persistFilterOption } from "../utils/storage";

const { Search } = Input;

interface Props {
  envConfig: any;
}

const DivRow = styled.div`
  display: flex;
  justify-content: space-evenly;
  flex-direction: column;
  align-items: center;

  @media only screen and ${breakpoint.device.sm} {
    flex-direction: row;
  }
`;

const LeftRow = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;

  @media only screen and ${breakpoint.device.sm} {
    flex-direction: row;
  }
`;

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  @media only screen and ${breakpoint.device.sm} {
    flex-direction: row;
  }
`;

export interface Filter {
  status?: string;
  fileStatus?: string;
  search?: string;
  count: number;
  page: number;
  sortColumn: string;
  sortDirection: string;
  refreshStamp: string;
  statusFilter?: number;
  statusId?: number;
  emailSubject?: string;
  emailAddress?: string;
  assignedTo?: string;
  assignedToName?: string;
  customerName?: string;
  st?: string;
  expand?: string;
  docType?: DocumentTypeEnum;
}

export interface BulkActions {
  emails: React.Key[];
  invoices: React.Key[];
  isModalOpen: boolean;
  hasSelected: boolean;
}

const SearchStyle = {
  margin: 5,
  display: "flex",
  flex: 1,
};

function Dashboard({ envConfig }: Props) {
  const searchFromLocation = useLocation().search;
  const urlParams = useMemo(() => new URLSearchParams(searchFromLocation), [searchFromLocation]);
  const dispatch = useDispatch();
  const history = useHistory();
  const emailStatusCounts = useAppSelector((state) => state.emails.statusCounts);
  const invoiceStatusCounts: StatusCounts[] = useAppSelector((state) => state.invoices.statusCounts);
  const assigneeCounts = useAppSelector((state) => state.invoices.assigneeCounts);
  const { features } = useContext(FeatureFlags);
  const showServiceTeamDropdownSearch = features.showServiceTeamDropdownSearch;

  const [bulkActions, setBulkActions] = useState<BulkActions>({
    emails: [],
    invoices: [],
    isModalOpen: false,
    hasSelected: false,
  });

  const [filter, setFilter] = useState<Filter | null>(null);
  const [emailAddress, setEmailAddress] = useState<string>("");
  const [emailSubject, setEmailSubject] = useState<string>("");
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [selectedServiceTeam, setSelectedServiceTeam] = useState<any>(null);

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      let newFilter: Partial<Filter> = {};
      const filterOpts = features?.filterOptions;

      if (filterOpts) {
        filterOpts.forEach((f: FilterOptModel) => {
          let paramValue;
          if (f.storage) {
            paramValue = urlParams.get(f.key) || sessionStorage.getItem(`${f.key}`) || f.value;
          } else {
            paramValue = urlParams.get(f.key) || f.value;
          }

          if (paramValue) {
            newFilter[f.label] = paramValue;
          }
        });
      }

      setEmailAddress(newFilter.emailAddress);
      setEmailSubject(newFilter.emailSubject);
      setFilter(newFilter as Filter);
      let newSelectedUser: any = null;
      if (newFilter.assignedTo && newFilter.assignedToName) {
        newSelectedUser = { value: newFilter.assignedTo, label: newFilter.assignedToName };
      }
      setSelectedUser(newSelectedUser);
      let newSelectedServiceTeam: any = null;
      if (newFilter.st) {
        newSelectedServiceTeam = { key: newFilter.st };
      }
      setSelectedServiceTeam(newSelectedServiceTeam);
      isInitialMount.current = false;
    }
  }, [features, urlParams]);

  useEffect(() => {
    if (filter === null) return;

    const controller = new AbortController();
    if (features?.pageLayout.displayEmailList) {
      dispatch(fetchEmailCounts({ controller }));
      dispatch(fetchEmails({ filter, controller }));
    } else {
      dispatch(fetchAllInvoices({ filter, controller }));
    }

    return () => {
      controller.abort();
    }
  }, [filter, dispatch, features]);

  /**
   * The counts depend on far less than the list does — paging and sorting do not change a count,
   * and neither count query even sends them. Keying the fetch on the whole filter re-ran two
   * cross-partition Cosmos GROUP BYs on every page and sort click for a byte-identical answer,
   * which was the bulk of the RU blowup in FOC-5821. Memoising on the scope key keeps the object
   * identity stable until something that actually moves a number changes.
   */
  const scopeKey = countsScopeKey(filter);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const countsScope = useMemo(() => countsScopeOf(filter), [scopeKey]);

  useEffect(() => {
    if (countsScope === null || features?.pageLayout.displayEmailList) return;

    const controller = new AbortController();
    dispatch(fetchInvoiceCounts({ controller, filter: countsScope }));
    if (features?.isAssignToEnabled) {
      dispatch(fetchAssigneeCounts({ controller, filter: countsScope }));
    }

    return () => {
      controller.abort();
    }
  }, [countsScope, dispatch, features]);

  const onFilterChange = useCallback((key: keyof Filter, value?: number | string | DocumentTypeEnum) => {
    const newState = { [key]: value };
    const newUrlParams = new URLSearchParams(urlParams);
    if (key !== "page" && key !== "sortDirection" && key !== "sortColumn") {
      newState.page = 1;
      newUrlParams.set("page", "1");
      persistFilterOption("page", 1, features.filterOptions);
    }

    // Update fileStatus if it contains a value not in status
    if (key === "status" && filter?.fileStatus && value?.toString() && !value.toString().includes(filter.fileStatus)) {
      const statuses = value.toString().split(',') ?? [];
      let newFileStatus = null;
      if (statuses.length === 1) {
        newFileStatus = statuses[0];
      }
      newState.fileStatus = newFileStatus;
      if (newFileStatus) {
        newUrlParams.set("fileStatus", newFileStatus);
      } else {
        newUrlParams.delete("fileStatus");
      }
      persistFilterOption("fileStatus", newFileStatus, features.filterOptions);
    }

    if (value) {
      newUrlParams.set(key, value.toString());
    } else {
      newUrlParams.delete(key);
    }
    history.push({ search: newUrlParams.toString() });

    persistFilterOption(key as string, value, features.filterOptions);

    setFilter((state) => ({ ...state, ...newState }));
  }, [urlParams, filter, history, features.filterOptions]);

  const onFilterChangeDebounced = useMemo(() => debounce(onFilterChange, DEBOUNCE_TIME), [onFilterChange]);

  useEffect(() => {
    return () => {
      onFilterChangeDebounced.cancel();
    };
  }, [onFilterChangeDebounced]);

  const handleOnSearchTags = useCallback(
    (tags: string[]) => {
      const criteria = tags.join(",");
      onFilterChange("search", criteria);
    },
    [onFilterChange]
  );

  const filterByUser = (value: any) => {
    if (value) {
      onFilterChange("assignedTo", value?.value);
      onFilterChange("assignedToName", value?.label);
    } else {
      onFilterChange("assignedTo", undefined);
      onFilterChange("assignedToName", undefined);
    }
    setSelectedUser(value);
  };

  const filterByServiceTeam = (value: any) => {
    if (value) {
      onFilterChange("st", value?.key);
    } else {
      onFilterChange("st", undefined);
    }
    setSelectedServiceTeam(value);
  };

  const handleChange = () => {
    setBulkActions((state) => ({ ...state, isModalOpen: true }));
  };

  const onSelectionChange = (value: React.Key[]) => {
    if (features?.pageLayout.displayEmailList) setBulkActions((state) => ({ ...state, emails: value, hasSelected: value.length > 0 }));
    else setBulkActions((state) => ({ ...state, invoices: value, hasSelected: value.length > 0 }));
  };

  const onClosePopup = (refresh: boolean) => {
    if (refresh) {
      const refreshStamp = `${new Date().getTime()}`;
      setFilter((state) => ({ ...state, refreshStamp: refreshStamp }));
      setBulkActions((state) => ({ ...state, emails: [], action: "Select", isModalOpen: false, hasSelected: false }));
    } else {
      setBulkActions((state) => ({ ...state, action: "Select", isModalOpen: false }));
    }
  };

  const fetchServiceTeamList = async (criteria: string): Promise<any[]> => {
    return searchServiceTeams(criteria).then((res: any) => {
      return res;
    });
  };

  useSignalR({ envConfig, id: null, readOnly: null });

  const isGlobalSearch = features?.isGlobalSearchBox || false;
  const displayEmailList = features?.pageLayout?.displayEmailList || false;

  if (features?.pageLayout.useDefaultLayout) {
    return (
      <Layout>
        <div style={{ flex: 1, overflow: "hidden" }}>
          {
            <BlankCard>
              <DivRow>
                <StatusFilters
                  statusCounts={features?.pageLayout.displayEmailList ? emailStatusCounts : invoiceStatusCounts}
                  filter={filter}
                  onFilterChange={onFilterChange}
                />
              </DivRow>
            </BlankCard>
          }
          <BlankCard>
            {!isGlobalSearch && (
              <DivRow>
                <Search
                  style={SearchStyle}
                  onChange={({ target }) => {
                    setEmailAddress(target.value);
                    onFilterChangeDebounced("emailAddress", target.value);
                  }}
                  defaultValue={""}
                  allowClear
                  placeholder="Email"
                  size="large"
                  loading={false}
                  value={emailAddress}
                />
                <Search
                  style={SearchStyle}
                  onChange={({ target }) => {
                    setEmailSubject(target.value);
                    onFilterChangeDebounced("emailSubject", target.value);
                  }}
                  defaultValue={""}
                  allowClear
                  placeholder="Subject"
                  size="large"
                  loading={false}
                  value={emailSubject}
                />
                <AssigneeSelect style={SearchStyle} selected={selectedUser} placeholder="Assigned To" onClear={() => filterByUser(null)} onChange={target => { if (target) filterByUser(target); }} />
                {showServiceTeamDropdownSearch && (
                  <DebounceSelect style={SearchStyle} selected={selectedServiceTeam} size="large" placeholder="Service Team" fetchOptions={fetchServiceTeamList} onClear={() => filterByServiceTeam(null)} onChange={target => { if (target) filterByServiceTeam(target); }} />
                )}
              </DivRow>
            )}
            {isGlobalSearch && (
              <DivRow>
                <TagInput placeholder="Search" style={SearchStyle} value={filter?.search ? filter?.search.split(",") : []} onSearch={handleOnSearchTags} />
                {showServiceTeamDropdownSearch && (
                  <DebounceSelect style={SearchStyle} selected={selectedServiceTeam} size="middle" placeholder="Service Team" fetchOptions={fetchServiceTeamList} onClear={() => filterByServiceTeam(null)} onChange={filterByServiceTeam} />
                )}
              </DivRow>
            )}
          </BlankCard>
          <BlankCard>
            <LeftRow>
              {features?.isAssignToEnabled && (
                <Button type="default" disabled={!bulkActions.hasSelected} onClick={handleChange}>
                  Assign To
                </Button>
              )}
            </LeftRow>
            <Row className="pt-20px">
              <Col span={24}>
                {displayEmailList && (
                  <EmailsTable
                    filter={filter}
                    onFilterChange={onFilterChange}
                    selectedEmails={bulkActions.emails}
                    onSelectionChange={onSelectionChange}
                  />
                )}
                {!displayEmailList && (
                  <InvoicesTable
                    filter={filter}
                    onFilterChange={onFilterChange}
                    selectedEmails={bulkActions.emails}
                    onSelectionChange={onSelectionChange}
                  />
                )}
              </Col>
            </Row>
          </BlankCard>
        </div>
        <AssignToModal
          isModalOpen={bulkActions.isModalOpen}
          emails={bulkActions.emails}
          invoices={bulkActions.invoices}
          isCustomer={false}
          closePopup={onClosePopup}
        ></AssignToModal>
      </Layout>
    );
  } else {
    return (
      <>
        <Row>
          <Col span={1} />
          <Col span={22} className="container">
            <Row>
              <Col span={24}>
                <SearchBar
                  statusCounts={invoiceStatusCounts}
                  filter={filter}
                  onFilterChange={onFilterChange}
                  showServiceTeamDropdownSearch={showServiceTeamDropdownSearch}
                  showDocumentTypeDropdown={features?.showDocumentTypeDropdown}
                  showStatusTypeDropdown={features?.isPendingSLValidationEnabled}
                  showAssignedToFilter={features?.isAssignToEnabled}
                  assigneeCounts={assigneeCounts}
                  onUserChange={filterByUser}
                />
              </Col>
            </Row>
            <Row className="pt-20px">
              <Col span={24}>
                {displayEmailList && (
                  <EmailsTable
                    filter={filter}
                    onFilterChange={onFilterChange}
                    selectedEmails={bulkActions.emails}
                    onSelectionChange={onSelectionChange}
                  />
                )}
                {!displayEmailList && (
                  <InvoicesTable
                    filter={filter}
                    onFilterChange={onFilterChange}
                    selectedEmails={bulkActions.emails}
                    onSelectionChange={onSelectionChange}
                  />
                )}
              </Col>
            </Row>
          </Col>
          <Col span={2} />
        </Row>
      </>
    );
  }
}

export default Dashboard;
